import React, { useState, useEffect } from 'react';
import { Alert, Box, FormControl, LinearProgress, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { EmojiEvents as EmojiEventsIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../services/api.service';
import { EmptyState } from '../ui/EmptyState';
import ActivityDetailsModal from './ActivityDetailsModal';
import { LeaderboardTable, LeaderboardRow } from './LeaderboardTable';

interface TeamMemberStats {
  userId: string;
  userName: string;
  userAvatar?: string;
  stravaUserId?: number;
  teamId: string;
  teamName: string;
  totalDistance: number;
  averagePace: number;
  bestPace: number;
  totalActivities: number;
  totalTracklog: number;
  rank?: number;
  // Admin only: flagged activities not yet dismissed.
  suspiciousActivityCount?: number;
}

interface Team {
  _id: string;
  name: string;
}

interface TeamMemberLeaderboardProps {
  contestId: string;
  teams: Team[];
  currentUserId?: string | null;
  // Bumped by the page after changes that affect standings, e.g. an activity sync.
  refreshKey?: number;
}

const TeamMemberLeaderboard: React.FC<TeamMemberLeaderboardProps> = ({ contestId, teams, currentUserId, refreshKey }) => {
  const { t } = useTranslation('contests');
  const [leaderboard, setLeaderboard] = useState<TeamMemberStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<string>('all');
  const [reloadKey, setReloadKey] = useState(0);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<TeamMemberStats | null>(null);
  const [memberActivities, setMemberActivities] = useState<any[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);

  useEffect(() => {
    const loadLeaderboard = async () => {
      try {
        setLoading(true);
        setError(false);
        const teamId = selectedTeam === 'all' ? undefined : selectedTeam;
        const response = await apiService.getTeamMemberLeaderboard(contestId, teamId, 100);
        setLeaderboard(response.members || []);
      } catch (err) {
        console.error('Failed to load team member leaderboard:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadLeaderboard();
  }, [contestId, selectedTeam, refreshKey, reloadKey]);

  const handleMemberClick = async (row: LeaderboardRow) => {
    const member = leaderboard.find(m => m.userId === row.key);
    if (!member) return;
    setSelectedMember(member);
    setModalOpen(true);
    setActivitiesLoading(true);

    try {
      const response = await apiService.getContestTeamMemberActivities(contestId, member.userId);
      setMemberActivities(response.activities || []);
    } catch (err) {
      console.error('Failed to load member activities:', err);
      setMemberActivities([]);
    } finally {
      setActivitiesLoading(false);
    }
  };

  // Refetch the drawer (record ids change after a Strava edit) and the open counts.
  const handleReviewSuspicious = async (activityId: string, dismissed: boolean) => {
    if (!selectedMember) return;
    await apiService.reviewSuspiciousActivity(contestId, activityId, dismissed);
    const response = await apiService.getContestTeamMemberActivities(contestId, selectedMember.userId);
    setMemberActivities(response.activities || []);
    setReloadKey(k => k + 1);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedMember(null);
    setMemberActivities([]);
  };

  const rows: LeaderboardRow[] = leaderboard.map((m, index) => ({
    key: m.userId,
    rank: m.rank || index + 1,
    name: m.userName,
    subtitle: m.teamName,
    avatarUrl: m.userAvatar,
    distance: m.totalDistance,
    count: m.totalActivities,
    averagePace: m.averagePace,
    fastestPace: m.bestPace,
    suspiciousCount: m.suspiciousActivityCount,
    isCurrentUser: !!currentUserId && m.userId === currentUserId,
  }));

  return (
    <Box>
      {teams.length > 1 && (
        <Box sx={{ p: '12px 16px', borderBottom: '1px solid var(--border)' }}>
          <FormControl size="small" sx={{ minWidth: 200, maxWidth: '100%' }}>
            <Select value={selectedTeam} onChange={(e: SelectChangeEvent<string>) => setSelectedTeam(e.target.value)} displayEmpty inputProps={{ 'aria-label': t('memberLeaderboard.filterByTeam') }}>
              <MenuItem value="all">{t('memberLeaderboard.allTeams')}</MenuItem>
              {teams.map(team => (
                <MenuItem key={team._id} value={team._id}>
                  {team.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {loading ? (
        <LinearProgress />
      ) : error ? (
        <Box sx={{ p: 2 }}>
          <Alert severity="error">{t('memberLeaderboard.loadError')}</Alert>
        </Box>
      ) : rows.length === 0 ? (
        <EmptyState icon={<EmojiEventsIcon />} title={t('memberLeaderboard.emptyTitle')} description={t('memberLeaderboard.emptyDescription')} />
      ) : (
        <LeaderboardTable rows={rows} countLabel={t('leaderboard.columns.activities')} onRowClick={handleMemberClick} />
      )}

      {selectedMember && (
        <ActivityDetailsModal
          open={modalOpen}
          onClose={handleModalClose}
          userName={selectedMember.userName}
          teamName={selectedMember.teamName}
          activities={memberActivities}
          loading={activitiesLoading}
          onReviewSuspicious={handleReviewSuspicious}
        />
      )}
    </Box>
  );
};

export default TeamMemberLeaderboard;
